<template>
    <div class="flex gap-4 overflow-x-auto pb-4">
        <!-- Column for each status -->
        <div 
            v-for="status in statusColumns" 
            :key="status" 
            class="flex-shrink-0 w-80 bg-muted/30 rounded-lg p-4"
        >
            <!-- Column Header -->
            <div class="flex items-center justify-between mb-4">
                <div class="flex items-center gap-2">
                    <span class="h-2.5 w-2.5 rounded-full" :style="getStatusDotStyle(status)" />
                    <h3 class="font-semibold">{{ getStatusLabel(status) }}</h3>
                </div>
                <Badge variant="secondary">{{ getTasksForStatus(status).length }}</Badge>
            </div>

            <!-- Task Cards -->
            <div class="space-y-3 min-h-[200px]">
                <div
                    v-for="task in getTasksForStatus(status)"
                    :key="task.id"
                    @click="emit('view-task', task.id)"
                    class="bg-background border rounded-lg p-4 cursor-pointer hover:border-primary transition-colors shadow-sm"
                >
                    <!-- Task Title -->
                    <h4 class="font-medium mb-2 line-clamp-2">{{ task.title }}</h4>

                    <!-- Task Metadata -->
                    <div class="space-y-2 text-sm">
                        <!-- Priority -->
                        <div v-if="task.priority" class="flex items-center gap-2">
                            <span class="font-medium" :style="{ color: getPriorityDisplay(task.priority).color }">
                                {{ getPriorityDisplay(task.priority).label }}
                            </span>
                        </div>

                        <!-- Assignee -->
                        <div v-if="getTaskAssignees(task).length" class="flex items-center gap-2">
                            <TaskAssigneeDisplay :assignees="getTaskAssignees(task)" compact />
                        </div>

                        <!-- Due Date -->
                        <div v-if="task.dueDate" class="flex items-center gap-2">
                            <span class="text-muted-foreground">📅</span>
                            <span :class="{ 'text-destructive font-medium': isOverdue(task.dueDate) }">
                                {{ formatDate(task.dueDate) }}
                            </span>
                        </div>

                        <!-- Project -->
                        <div v-if="task.projectId && projectOptions" class="flex items-center gap-2">
                            <span class="text-muted-foreground">📁</span>
                            <span class="text-xs">{{ getProjectName(task.projectId) }}</span>
                        </div>
                    </div>

                    <!-- Quick Actions -->
                    <div class="flex gap-2 mt-3 pt-3 border-t">
                        <Button
                            size="sm"
                            variant="outline"
                            class="flex-1"
                            @click.stop="moveTaskToPreviousStatus(task)"
                            :disabled="!canMoveToPrevious(task)"
                        >
                            ←
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            class="flex-1"
                            @click.stop="moveTaskToNextStatus(task)"
                            :disabled="!canMoveToNext(task)"
                        >
                            →
                        </Button>
                    </div>
                </div>

                <!-- Empty State for Column -->
                <div 
                    v-if="getTasksForStatus(status).length === 0"
                    class="text-center text-muted-foreground text-sm py-8"
                >
                    No tasks in {{ status.toLowerCase() }}
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
/**
 * KanbanBoard Component
 * 
 * A kanban-style board for visualizing and managing tasks by status.
 * Provides a column-based view with quick status transitions.
 * 
 * Features:
 * - Status columns with task cards
 * - Visual priority indicators
 * - Quick status navigation (← →  buttons)
 * - Task metadata display (assignee, due date, project)
 * - Overdue date highlighting
 * - Click to view task details
 * - Empty state for each column
 * - Task count badges per column
 * 
 * Design Patterns:
 * - Read-only view: Tasks are displayed but not draggable
 * - Quick actions: Arrow buttons for status transitions
 * - Responsive: Horizontal scroll for many columns
 * - Consistent with table view: Same data, different visualization
 * 
 * Future Enhancements:
 * - Drag and drop support using VueDraggable
 * - Custom status columns
 * - Column filtering
 * - Swimlanes (group by assignee/project)
 * 
 * @example Usage
 * <KanbanBoard
 *   :tasks="filteredTasks"
 *   :status-columns="['Todo', 'In Progress', 'Done']"
 *   :priority-options="priorityOptions"
 *   @view-task="handleViewTask"
 *   @task-updated="handleTaskUpdated"
 * />
 */
import { computed } from 'vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import TaskAssigneeDisplay from './TaskAssigneeDisplay.vue';
import { getTaskStatusOption } from '~/lib/taskConfig';
import { resolveTaskAssignees } from '~/lib/taskAssignees';
import type { Task, TaskPriority, TenantTaskStatusOption } from '~/types/models';

interface Props {
    /** Array of tasks to display */
    tasks: Task[];
    /** Status columns to show (in order) */
    statusColumns?: string[];
    /** Status metadata for labels/colors */
    statusOptionDetails?: TenantTaskStatusOption[];
    /** Priority options for color coding */
    priorityOptions?: Array<{ value: TaskPriority; label: string; color: string }>;
    /** Available projects for name lookup */
    projectOptions?: Array<{ id: string; name: string }>;
}

const props = withDefaults(defineProps<Props>(), {
    statusColumns: () => ['Todo', 'In Progress', 'Blocked', 'Done'],
    statusOptionDetails: () => [],
    priorityOptions: () => [
        { value: 'low', label: 'Low', color: '#2563eb' },
        { value: 'medium', label: 'Medium', color: '#d97706' },
        { value: 'high', label: 'High', color: '#ea580c' },
        { value: 'critical', label: 'Critical', color: '#dc2626' }
    ],
    projectOptions: () => []
});

const emit = defineEmits<{
    /** Emitted when user clicks to view task details */
    (e: 'view-task', taskId: string): void;
    /** Emitted when task status is updated */
    (e: 'task-updated', taskId: string, status: string): void;
}>();

// ========================================================================
// COMPUTED PROPERTIES
// ========================================================================

/**
 * Get tasks for a specific status column
 * 
 * @param status - Status to filter by
 * @returns Array of tasks with that status
 */
function getTasksForStatus(status: string): Task[] {
    return props.tasks.filter(task => task.status === status);
}

/**
 * Check if task can move to previous status
 * 
 * @param task - Task to check
 * @returns True if not already at first status
 */
function canMoveToPrevious(task: Task): boolean {
    const currentIndex = props.statusColumns.indexOf(task.status || '');
    return currentIndex > 0;
}

/**
 * Check if task can move to next status
 * 
 * @param task - Task to check
 * @returns True if not already at last status
 */
function canMoveToNext(task: Task): boolean {
    const currentIndex = props.statusColumns.indexOf(task.status || '');
    return currentIndex >= 0 && currentIndex < props.statusColumns.length - 1;
}

// ========================================================================
// METHODS
// ========================================================================

/**
 * Move task to previous status column
 * 
 * @param task - Task to move
 */
function moveTaskToPreviousStatus(task: Task) {
    const currentIndex = props.statusColumns.indexOf(task.status || '');
    if (currentIndex > 0) {
        const newStatus = props.statusColumns[currentIndex - 1];
        emit('task-updated', task.id, newStatus);
    }
}

/**
 * Move task to next status column
 * 
 * @param task - Task to move
 */
function moveTaskToNextStatus(task: Task) {
    const currentIndex = props.statusColumns.indexOf(task.status || '');
    if (currentIndex >= 0 && currentIndex < props.statusColumns.length - 1) {
        const newStatus = props.statusColumns[currentIndex + 1];
        emit('task-updated', task.id, newStatus);
    }
}

/**
 * Get priority display metadata
 * 
 * @param priority - Priority value
 * @returns Label and colour
 */
function getPriorityDisplay(priority?: TaskPriority): { label: string; color: string } {
    if (!priority) return { label: 'None', color: 'var(--muted-foreground)' };
    const option = props.priorityOptions?.find(p => p.value === priority);
    return {
        label: option?.label || priority,
        color: option?.color || 'var(--muted-foreground)',
    };
}

function getStatusLabel(status: string): string {
    return getTaskStatusOption(status, props.statusOptionDetails || [])?.label || status;
}

function getStatusDotStyle(status: string) {
    return {
        backgroundColor: getTaskStatusOption(status, props.statusOptionDetails || [])?.color || 'var(--muted-foreground)',
    };
}

function getTaskAssignees(task: Task) {
    return resolveTaskAssignees(task);
}

/**
 * Get project name from ID
 * 
 * @param projectId - Project ID
 * @returns Project name or fallback
 */
function getProjectName(projectId?: string): string {
    if (!projectId) return 'No Project';
    return props.projectOptions?.find(p => p.id === projectId)?.name || 'Unknown';
}

/**
 * Check if task is overdue
 * 
 * @param dueDate - Due date string
 * @returns True if past due
 */
function isOverdue(dueDate?: string): boolean {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
}

/**
 * Format date for display
 * 
 * @param dateString - ISO date string
 * @returns Formatted date
 */
function formatDate(dateString?: string): string {
    if (!dateString) return '';
    try {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    } catch {
        return dateString;
    }
}
</script>
